<template>
  <div>
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">{{ $t('hotelTags.title') }}</h6>
          <nav class="d-none d-md-inline-block ml-md-4" aria-label="breadcrumb">
            <ol class="breadcrumb breadcrumb-links breadcrumb-dark">
              <li class="breadcrumb-item"><a href="#"><i class="ai ai-home"></i></a></li>
            </ol>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <div class="row">
        <div class="col">
          <card>
            <div class="text-right">
              <base-button 
                type="primary"
                outline
                @click="openTagManagement"
                :disabled="!currentHotel"
              >
                <i class="ai ai-edit mr-2"></i>
                {{ $t('hotelTags.manageTags') }}
              </base-button>
            </div>
            <div v-if="loadingHotels" class="text-center py-4">
              <i class="fa fa-spinner fa-spin fa-2x text-primary"></i>
              <p class="mt-2">{{ $t('hotelTags.loadingHotels') }}</p>
            </div>

            <el-tabs
              v-else-if="hotels.length > 0"
              v-model="selectedHotelId"
              :class="{'hide-tab-pane': hotels.length === 1}"
              @tab-click="hotelChanged">
              <el-tab-pane
                v-for="hotel in hotels"
                :key="hotel.id"
                :label="hotel.name"
                :name="hotel.id.toString()">

                <div v-if="loading" class="text-center py-4">
                  <i class="fa fa-spinner fa-spin fa-2x text-primary"></i>
                  <p class="mt-2">{{ $t('hotelTags.loadingTags') }}</p>
                </div>

                <div v-else>
                  <el-table
                    v-if="hotelTags.length > 0"
                    :data="hotelTags"
                    class="table-responsive table-flush"
                    header-row-class-name="thead-light"
                    border>
                    
                    <el-table-column :label="$t('hotelTags.tagName')" min-width="200">
                      <template slot-scope="{row}">
                        <div class="d-flex align-items-center">
                          <i 
                            :class="row.assist_admin ? 'ai ai-shield text-warning mr-2' : 'ai ai-tag text-primary mr-2'"
                          ></i>
                          <strong>{{ row.name }}</strong>
                        </div>
                      </template>
                    </el-table-column>

                    <el-table-column :label="$t('hotelTags.type')">
                      <template slot-scope="{row}">
                        <span 
                          :class="row.assist_admin ? 'badge badge-warning' : 'badge badge-primary'"
                        >
                          {{ row.assist_admin ? $t('hotelTags.general') : $t('hotelTags.optional') }}
                        </span>
                      </template>
                    </el-table-column>

                    <el-table-column v-if="userIsAssistAdmin" :label="$t('hotelTags.actions')" width="160">
                      <template slot-scope="{row}">

                        <base-button
                          type="danger"
                          size="sm"
                          @click="removeTag(row.id)"
                        >
                          <i class="ai ai-remove"></i>
                          {{ $t('hotelTags.remove') }}
                        </base-button>
                      </template>
                    </el-table-column>

                  </el-table>

                  <div v-else class="text-center py-5">
                    <i class="ai ai-tag display-3 text-muted"></i>
                    <h4 class="text-muted mt-3">{{ $t('hotelTags.noTagsAssigned') }}</h4>
                    <p class="text-muted">{{ $t('hotelTags.noTagsDescription') }}</p>
                  </div>
                </div>

              </el-tab-pane>
            </el-tabs>

            <div v-else class="text-center py-5">
              <i class="ai ai-building display-3 text-muted"></i>
              <h4 class="text-muted mt-3">{{ $t('hotelTags.noHotelsFound') }}</h4>
              <p class="text-muted">{{ $t('hotelTags.noHotelsDescription') }}</p>
            </div>

          </card>
        </div>
      </div>
    </div>

    <modal :show.sync="showManageDialog" body-classes="pt-0" modal-classes="modal-dialog-centered modal-lg">
      <h4 class="modal-title" slot="header">{{ $t('hotelTags.manageHotelTags') }}</h4>
      
      <div v-if="loadingAllTags" class="text-center py-4">
        <i class="fa fa-spinner fa-spin fa-2x text-primary"></i>
        <p class="mt-2">{{ $t('hotelTags.loadingTags') }}</p>
      </div>
      
      <div v-else class="max-height-300 overflow-auto">
        <div class="form-group mb-3">
          <small class="text-muted">
            <i class="ai ai-info-circle mr-1"></i>
            {{ $t('hotelTags.modalDescription') }} <i class="ai ai-shield text-secondary"></i> {{ $t('hotelTags.modalDescriptionSuffix') }}
          </small>
        </div>
        
        <div 
          v-for="tag in sortedAllTags" 
          :key="tag.id"
          class="d-flex align-items-center justify-content-between py-2 px-3 mb-2 border rounded"
          :class="tag.assist_admin ? 'border-secondary bg-very-light' : 'border-light'"
        >
          <div class="d-flex align-items-center">
            <el-checkbox
              v-model="tagStates[tag.id]"
              :disabled="tag.assist_admin && !userIsAssistAdmin"
              @change="onTagStateChange(tag.id, $event)"
            ></el-checkbox>
            
            <div class="ml-3">
              <div class="d-flex align-items-center">
                <i 
                  :class="tag.assist_admin ? 'ai ai-shield text-secondary mr-2' : 'ai ai-tag text-primary mr-2'"
                ></i>
                <strong>{{ tag.name }}</strong>
                                 <span 
                   v-if="tag.assist_admin"
                   class="badge badge-secondary ml-2"
                 >
                   {{ $t('hotelTags.admin') }}
                 </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template slot="footer">
        <base-button type="secondary" @click="closeManageDialog">{{ $t('hotelTags.cancel') }}</base-button>
        <base-button 
          type="success" 
          @click="saveTagChanges"
          :disabled="!hasChanges || saving"
        >
          <i v-if="saving" class="fa fa-spinner fa-spin mr-2"></i>
          <i v-else class="ai ai-save mr-2"></i>
          {{ $t('hotelTags.saveChanges') }}
        </base-button>
      </template>
    </modal>


  </div>
</template>

<script>
import { Modal } from '@/components'
import { Select, Option, Tabs, TabPane, Table, TableColumn, Checkbox } from 'element-ui'

export default {
  name: 'HotelTags',
  components: {
    Modal,
    [Select.name]: Select,
    [Option.name]: Option,
    [Tabs.name]: Tabs,
    [TabPane.name]: TabPane,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Checkbox.name]: Checkbox
  },
  data() {
    return {
      loadingHotels: true,
      loading: false,
      loadingAllTags: false,
      saving: false,
      hotels: [],
      selectedHotelId: null,
      hotelTags: [],
      allTags: [],
      showManageDialog: false,
      tagStates: {},
      originalTagStates: {}
    }
  },
  computed: {
    currentHotel() {
      if (!this.selectedHotelId) return null
      return this.hotels.find(hotel => hotel.id === parseInt(this.selectedHotelId))
    },
    
    sortedAllTags() {
      return this.allTags.slice().sort((a, b) => a.rank - b.rank)
    },
    
    userIsAssistAdmin() {
      return this.$store.getters.getUser && this.$store.getters.getUser.assist_admin
    },
    
    hasChanges() {
      return JSON.stringify(this.tagStates) !== JSON.stringify(this.originalTagStates)
    }
  },
  async mounted() {
    await this.loadHotels()
  },
  methods: {
    async loadHotels() {
      this.loadingHotels = true
      try {
        const response = await this.$axios.get('/api/hotels')
        this.hotels = response.data.data
        
        if (this.hotels.length > 0) {
          this.selectedHotelId = this.hotels[0].id.toString()
          await this.loadData()
        }
      } catch (error) {
        this.$notify({
          title: this.$t('hotelTags.failedToLoadHotels'),
          type: 'danger'
        })
      } finally {
        this.loadingHotels = false
      }
    },

    async hotelChanged(tab) {
      this.selectedHotelId = tab.name
      await this.loadData()
    },

    async loadData() {
      if (!this.currentHotel) return
      
      this.loading = true
      try {
        await this.loadHotelTags()
      } catch (error) {
        this.$notify({
          title: this.$t('hotelTags.failedToLoadData'),
          type: 'danger'
        })
      } finally {
        this.loading = false
      }
    },
    
    async loadHotelTags() {
      try {
        const response = await this.$axios.get(`/api/hotels/${this.currentHotel.id}/tags`)
        this.hotelTags = response.data.data
      } catch (error) {
        throw new Error('Failed to load hotel tags')
      }
    },
    
    async loadAllTags() {
      try {
        const response = await this.$axios.get('/api/available-tags')
        this.allTags = response.data.data
      } catch (error) {
        throw new Error('Failed to load all tags')
      }
    },
    
    async openTagManagement() {
      if (!this.currentHotel) return
      
      this.showManageDialog = true
      this.loadingAllTags = true
      
      try {
        await this.loadAllTags()
        this.initializeTagStates()
      } catch (error) {
        this.$notify({
          title: this.$t('hotelTags.failedToLoadTags'),
          type: 'danger'
        })
        this.showManageDialog = false
      } finally {
        this.loadingAllTags = false
      }
    },
    
    initializeTagStates() {
      const states = {}
      const currentTagIds = this.hotelTags.map(tag => tag.id)
      
      this.allTags.forEach(tag => {
        states[tag.id] = currentTagIds.includes(tag.id)
      })
      
      this.tagStates = states
      this.originalTagStates = JSON.parse(JSON.stringify(states))
    },
    
    onTagStateChange(tagId, checked) {
      this.tagStates[tagId] = checked
    },
    
    async saveTagChanges() {
      if (!this.currentHotel || !this.hasChanges) return
      
      this.saving = true
      try {
        const changes = []
        
        for (const tagId in this.tagStates) {
          const current = this.tagStates[tagId]
          const original = this.originalTagStates[tagId]
          
          if (current !== original) {
            changes.push({
              tag_id: parseInt(tagId),
              action: current ? 'add' : 'remove'
            })
          }
        }
        
        await this.$axios.post(`/api/hotels/${this.currentHotel.id}/tags/batch`, {
          changes: changes
        })
        
        this.$notify({
          title: this.$t('hotelTags.tagsSuccessfullyUpdated')
        })
        await this.loadData()
        this.closeManageDialog()
      } catch (error) {
        this.$notify({
          title: (error.response && error.response.data && error.response.data.message) || this.$t('hotelTags.failedToSaveChanges'),
          type: 'danger'
        })
      } finally {
        this.saving = false
      }
    },
    
    async removeTag(tagId) {
      if (!this.currentHotel) return
      
      try {
        await this.$axios.delete(`/api/hotels/${this.currentHotel.id}/tags/${tagId}`)
        this.$notify({
          title: this.$t('hotelTags.tagSuccessfullyRemoved')
        })
        await this.loadData()
      } catch (error) {
        this.$notify({
          title: (error.response && error.response.data && error.response.data.message) || this.$t('hotelTags.failedToRemoveTag'),
          type: 'danger'
        })
      }
    },
    
    closeManageDialog() {
      this.showManageDialog = false
      this.tagStates = {}
      this.originalTagStates = {}
      this.allTags = []
    },
    

  }
}
</script>

<style scoped>
.hide-tab-pane .el-tabs__header {
  display: none;
}

.max-height-300 {
  max-height: 300px;
}

.overflow-auto {
  overflow-y: auto;
}

.bg-very-light {
  background-color: #f8f9fa !important;
}


</style>
